import React, { useState, useEffect, useCallback, useMemo, useContext } from 'react';
import { WindowSizeContext } from '/src/App';
import { AreaDataContext } from '/src/Views/PropertyExplorer/PropertyExplorer';
import HelpMeChooseQuestionnaire from './HelpMeChooseQuestionnaire/HelpMeChooseQuestionnaire';
import ComparisonChart from './ComparisonChart/ComparisonChart';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    useDraggable,
    useDroppable,
} from '@dnd-kit/core';
import {
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    arrayMove,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import ProjectsListCard from '/src/Components/ProjectsListCard/ProjectsListCard';
import { ChevronsUp } from 'lucide-react';

import {
    restrictToVerticalAxis,
    restrictToFirstScrollableAncestor
} from '@dnd-kit/modifiers';
import './style.css';
import debounce from 'lodash/debounce';

const DragHandle = () => {
    return (
        <div className="help-me-choose__drag-handle">
            ⋮⋮
        </div>
    );
};

const SortableFilter = ({ id, children }) => {
    const { deviceType } = useContext(WindowSizeContext);
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
    } = useSortable({ id });

    return (
        <div
            ref={setNodeRef}
            className="help-me-choose__filter"
            style={{
                transform: CSS.Transform.toString(transform),
                transition,
            }}
            {...attributes}
        >
            <div className="help-me-choose__filter-content">
                {children}
            </div>
        </div>
    );
};

const DraggableFactor = ({ factor, children }) => {
    const { attributes, listeners, setNodeRef, transform } = useDraggable({
        id: factor.id,
        data: factor
    });

    const style = transform ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
    } : undefined;

    return (
        <div
            ref={setNodeRef}
            {...listeners}
            {...attributes}
            className="help-me-choose__multi-projects-filters-draggable-item"
            style={style}
        >
            {/* <span>{factor.label}</span> */}
            {children}
        </div>
    );
};

const DroppableArea = ({ id, children }) => {
    const { setNodeRef, isOver } = useDroppable({
        id: id
    });

    return (
        <div
            ref={setNodeRef}
            className={`help-me-choose__multi-projects-filters-droppable-item-container ${isOver ? 'is-over' : ''}`}
        >
            {children}
        </div>
    );
};

const HelpMeChoose = ({ projects, propertyData, handleProjectClick }) => {
    const { deviceType } = useContext(WindowSizeContext);
    const memoizedProjects = useMemo(() => projects, [projects]);
    const filterOptions = [
        {
            id: 'buget',
            label: 'Budget'
        },

        {
            id: 'diy',
            label: 'DIY-Friendly Only?'
        },
        {
            id: 'requires_discounts',
            label: 'Requires Discounts'
        },
        // {
        //     id: 'max_projects',
        //     label: 'Max Number of Projects'
        // },
        // {
        //     id: 'payback',
        //     label: 'Max Time Till Payback'
        // },
        // {
        //     id: 'house_category',
        //     label: 'Parts of House'
        // }
    ]

    // console.log(projects)

    // Sum the project_total_cost from all of the projects
    const totalCostAllProjects = projects.reduce((acc, project) => acc + project.project_total_cost, 0);

    // Get all of the unqiue values for project_home_category from all of the projects
    const uniqueHomeCategories = [...new Set(projects.map(project => project.project_home_category))];

    const colorWheel = ["#954BDB", "#53C7ED", "#EDA153", "#53EDBF", "#688C98", "#EDC453", "#5D6E69", "#C3FCFD", "#F524BF", "#86C513", "#744687", "#1231C6", "#096750", "#95B396", "#E24618", "#4390BE", "#78D8CC", "#4A6B73", "#450155", "#46E966"]

    // Sort the projects by project_total_payback in descending order and add a new key called project_total_discounts which is just the length of the project_discounts_company_specific array
    const sortedProjects = projects
        .map((project, index) => ({
            ...project,
            project_has_discounts: project.project_discounts_company_specific.length > 0,
            project_total_discounts: project.project_discounts_company_specific.length,
            color: (index < colorWheel.length - 1) ? colorWheel[index] : colorWheel[index % colorWheel.length],
            project_complexity_refined: project.project_complexity * 10,
            project_disruption_refined: project.project_disruption * 10,
        }))
        .sort((a, b) => b.project_total_payback - a.project_total_payback);

    console.log("SORTED PROJECTS", sortedProjects);

    // Get the total number of projects
    const totalProjects = projects.length;

    const [processedProjects, setProcessedProjects] = useState(sortedProjects);

    // Set the original values in a const so we can easily reset them 
    const originalInputValues = {
        budgetValue: totalCostAllProjects,
        paybackYears: 10,
        isDiyOnly: false,
        requiresDiscounts: false,
        selectedCategories: uniqueHomeCategories,
        filteredProjects: sortedProjects,
        maxProjects: totalProjects
    }

    const [selectedFilters, setSelectedFilters] = useState([]);
    const [filteredProjects, setFilteredProjects] = useState(originalInputValues.filteredProjects);
    const [filterOrder, setFilterOrder] = useState(filterOptions.map(f => f.id));
    const [budgetValue, setBudgetValue] = useState(originalInputValues.budgetValue);
    const [paybackYears, setPaybackYears] = useState(originalInputValues.paybackYears);
    const [maxNumberOfProjects, setMaxNumberOfProjects] = useState(originalInputValues.maxProjects);
    const [isDiyOnly, setIsDiyOnly] = useState(originalInputValues.isDiyOnly);
    const [requiresDiscounts, setRequiresDiscounts] = useState(originalInputValues.requiresDiscounts);
    const [selectedCategories, setSelectedCategories] = useState(originalInputValues.selectedCategories);
    const [areThereChanges, setAreThereChanges] = useState(false);
    const [questionnaireCompleted, setQuestionnaireCompleted] = useState(false);
    const [enrichedHouseAreasWithProjects, setEnrichedHouseAreasWithProjects] = useState(null);
    const [multiProjectsView, setMultiProjectsView] = useState('compare');
    const [multiProjectsFilterOne, setMultiProjectsFilterOne] = useState(null);
    const [multiProjectsFilterTwo, setMultiProjectsFilterTwo] = useState(null);
    const [availableFactors, setAvailableFactors] = useState([]);
    const [showProjectFilterOptions, setShowProjectFilterOptions] = useState(false);
    const [hoveredProjectId, setHoveredProjectId] = useState(null);

    // Separate state for immediate UI feedback
    const [immediateBudgetValue, setImmediateBudgetValue] = useState(0);
    const [immediatePaybackYears, setImmediatePaybackYears] = useState(1);
    const [immediateMaxProjects, setImmediateMaxProjects] = useState(1);

    // Add this to your other state declarations in HelpMeChoose
    const [isDragging, setIsDragging] = useState(false);

    // useEffect to close the filter options when the hoveredProjectId is changed
    useEffect(() => {
        if (hoveredProjectId) {
            setShowProjectFilterOptions(false);
        }
    }, [hoveredProjectId]);

    // UseEffect to monitor for property_areas_threatened and refinedProjectsList changes, and update the propertyAreasWithProjects state
    useEffect(() => {
        if (propertyData?.data?.property_areas_threatened?.house_categories_with_risks.length && projects.length) {

            const property_areas_threatened = propertyData?.data?.property_areas_threatened?.house_categories_with_risks || [];

            const totalRisk = property_areas_threatened.reduce((sum, area) => {
                return sum + (area.rebuild_risk_weighted_likelihood_est || 0);
            }, 0);

            const enrichedAreas = property_areas_threatened.map(area => {

                const risk_pct_doughtnut = totalRisk ?
                    ((area.rebuild_risk_weighted_likelihood_est || 0) / totalRisk) * 100 :
                    0

                // Find all matching projects for this area
                const matchingProjects = projects.filter(project =>
                    project.project_home_category_id === area.id
                );

                // Calculate total discounts from all matching projects
                const totalDiscounts = matchingProjects.reduce((sum, project) =>
                    sum + (project.project_discounts_company_logos?.length || 0), 0
                );

                // Sort the matching projects by project_payoff_by_scenario[0].by_total.total
                matchingProjects.sort((a, b) => b.project_payoff_by_scenario[0].by_total.total - a.project_payoff_by_scenario[0].by_total.total);

                return {
                    ...area,
                    num_projects: matchingProjects.length,
                    num_discounts: totalDiscounts,
                    projects_list: matchingProjects,
                    risk_level_adjusted: risk_pct_doughtnut > 10 ? 'high' : risk_pct_doughtnut > 5 ? 'medium' : 'low'
                };
            });

            setEnrichedHouseAreasWithProjects(enrichedAreas);
        }
    }, [propertyData, projects]);

    // Debounced setters for actual computation
    const debouncedSetBudgetValue = useCallback(
        debounce((value) => setBudgetValue(value), 300),
        []
    );

    const debouncedSetPaybackYears = useCallback(
        debounce((value) => setPaybackYears(value), 300),
        []
    );

    const debouncedSetMaxProjects = useCallback(
        debounce((value) => setMaxNumberOfProjects(value), 300),
        []
    );

    // Get the total cost of the FILTERED projects
    const totalCostFilteredProjects = filteredProjects.reduce((acc, project) => acc + project.project_total_cost, 0);

    // Get all of the values for the project_home_category from the filtered projects
    const homeCategoriesFilteredProjects = [...new Set(filteredProjects.map(project => project.project_home_category))];

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    // Add these handlers
    const handleDragStart = () => {
        setIsDragging(true);
    };

    const handleDragCancel = () => {
        setIsDragging(false);
    };

    // Modify your existing handleDragEnd
    const handleDragEnd = (event) => {
        setIsDragging(false);  // Add this line
        const { active, over } = event;
        if (!over) return;

        const draggedFactor = comparisonFactors.find(f => f.id === active.id);

        if (over.id === 'drop-area-1') {
            setMultiProjectsFilterOne(draggedFactor);
        } else if (over.id === 'drop-area-2') {
            setMultiProjectsFilterTwo(draggedFactor);
        }
    };

    const availableFilters = filterOptions
        .filter(filter => !selectedFilters.includes(filter.id))
        .sort((a, b) => filterOrder.indexOf(a.id) - filterOrder.indexOf(b.id));

    // Get max budget value
    const maxBudget = Math.min(totalCostAllProjects, totalCostFilteredProjects);

    // Create a function for adding the selected filters to the selectedFilters array
    const addSelectedFilter = (filterId) => {
        setSelectedFilters(prev => [...prev, filterId]);
    }

    // Create a function for removing the selected filters from the selectedFilters array
    const removeSelectedFilter = (filterId) => {
        setSelectedFilters(prev => prev.filter(id => id !== filterId));
    }


    // Shared handler for filtering the projects
    const filterProjects = (sortedProjects, budget, isDiyOnly, selectedCategories, requiresDiscounts) => {
        return sortedProjects.filter(project => {
            return project.project_total_cost <= budget &&
                (isDiyOnly ? project.project_diy_friendly : true) &&
                selectedCategories.includes(project.project_home_category) &&
                (requiresDiscounts ? project.project_discounts_company_specific.length > 0 : true);
        });
    }


    // Create a useEffect that listens for changes to budgetValue, paybackYears, isDiyOnly, selectedCategories and then filters the projects based on this
    useEffect(() => {
        // First apply the simple filters
        // Budget < project_total_cost
        // DIY-Friendly Only? < project_diy_friendly
        // Discounts < requiresDiscounts

        const simpleFilteredProjects = filterProjects(processedProjects, budgetValue, isDiyOnly, selectedCategories, requiresDiscounts);

        // Sort projects by payback efficiency (payback per dollar) for greedy approach
        const newSortedProjects = [...simpleFilteredProjects].sort((a, b) =>
            (b.project_total_payback / b.project_total_cost) -
            (a.project_total_payback / a.project_total_cost)
        );

        // Use a greedy approach for initial load, switch to DP only when user interacts
        const findInitialCombination = (projects, budget) => {
            let remainingBudget = budget;
            let selectedProjects = [];

            for (const project of projects) {
                if (remainingBudget >= project.project_total_cost) {
                    selectedProjects.push(project);
                    remainingBudget -= project.project_total_cost;
                }
                if (remainingBudget <= 0) break;
            }

            return selectedProjects;
        };

        // Use simpler algorithm for initial load
        const initialProjects = findInitialCombination(
            newSortedProjects,
            Math.ceil(budgetValue)
        );

        setFilteredProjects(initialProjects);

    }, [budgetValue, isDiyOnly, selectedCategories, processedProjects, requiresDiscounts]);

    // Separate effect for optimal calculation after user interaction
    useEffect(() => {
        if (!areThereChanges || !questionnaireCompleted) return; // Only run expensive calculation after user interaction

        // First apply the simple filters
        const simpleFilteredProjects = filterProjects(processedProjects, budgetValue, isDiyOnly, selectedCategories, requiresDiscounts);

        // Helper function to calculate total cost and payback for a combination
        const calculateTotals = (combination) => {
            return combination.reduce((acc, project) => ({
                cost: acc.cost + project.project_total_cost,
                payback: acc.payback + project.project_total_payback
            }), { cost: 0, payback: 0 });
        };

        // Find optimal combination using dynamic programming
        const findOptimalCombination = (projects, budget, maxProjects) => {
            const n = projects.length;
            // Create a 3D array for dynamic programming: [items][budget][numProjects]
            const dp = Array(n + 1).fill().map(() =>
                Array(budget + 1).fill().map(() =>
                    Array(maxProjects + 1).fill().map(() => ({ payback: 0, projects: [] }))
                )
            );

            for (let i = 1; i <= n; i++) {
                const project = projects[i - 1];
                for (let b = 0; b <= budget; b++) {
                    for (let p = 0; p <= maxProjects; p++) {
                        // Don't include this project
                        dp[i][b][p] = { ...dp[i - 1][b][p] };

                        // Check if we can include this project (both budget and project count constraints)
                        if (project.project_total_cost <= b && p > 0) {
                            const remainingBudget = b - project.project_total_cost;
                            const newPayback = dp[i - 1][remainingBudget][p - 1].payback + project.project_total_payback;

                            if (newPayback > dp[i][b][p].payback) {
                                dp[i][b][p] = {
                                    payback: newPayback,
                                    projects: [...dp[i - 1][remainingBudget][p - 1].projects, project]
                                };
                            }
                        }
                    }
                }
            }

            // Find the best combination across all possible project counts up to maxProjects
            let bestCombination = { payback: 0, projects: [] };
            for (let p = 0; p <= maxProjects; p++) {
                if (dp[n][budget][p].payback > bestCombination.payback) {
                    bestCombination = dp[n][budget][p];
                }
            }

            return bestCombination.projects;
        };

        // Round budget up to nearest whole number for the algorithm
        const roundedBudget = Math.ceil(budgetValue);

        // Get the optimal combination
        const optimalProjects = findOptimalCombination(simpleFilteredProjects, roundedBudget, maxNumberOfProjects);

        setFilteredProjects(optimalProjects);
    }, [areThereChanges, budgetValue, paybackYears, isDiyOnly, selectedCategories, projects, maxNumberOfProjects]);


    // Create a clear button that resets all of the filters to the original values
    const clearFilters = () => {
        setSelectedFilters([]);
        setBudgetValue(originalInputValues.budgetValue);
        setPaybackYears(originalInputValues.paybackYears);
        setIsDiyOnly(originalInputValues.isDiyOnly);
        setSelectedCategories(originalInputValues.selectedCategories);
    }

    // Create a useEffect that listens for budgetValue, paybackYears, isDiyOnly, selectedCategories and if they are different from the original then sets the areThereChanges to true
    useEffect(() => {
        if (
            budgetValue !== originalInputValues.budgetValue ||
            // paybackYears !== originalInputValues.paybackYears ||
            isDiyOnly !== originalInputValues.isDiyOnly ||
            requiresDiscounts !== originalInputValues.requiresDiscounts
            // selectedCategories !== originalInputValues.selectedCategories
        ) {
            setAreThereChanges(true);
        }
    }, [budgetValue, isDiyOnly, requiresDiscounts]);

    const [localBudgetValue, setLocalBudgetValue] = useState(0);

    // Create a debounced version of the actual calculation handler
    const debouncedCalculation = useCallback(
        debounce((value) => {
            setImmediateBudgetValue(value);
            debouncedSetBudgetValue(value);
        }, 1000), // Wait 1000ms after the user stops sliding
        [] // Empty dependency array since we want this to be stable
    );

    const renderBudgetInput = (type) => {
        return (
            <>
                {type === 'comparison2' ? (
                    <div className="help-me-choose__input-wrapper-comparison-frame">

                        <div className={`help-me-choose__input-wrapper ${type}`}>
                            <div className="help-me-choose__filter-label comparison-label">Your Budget: </div>
                            <span className="help-me-choose__input-value">
                                {localBudgetValue === 0 ? '$' + totalCostAllProjects.toLocaleString() : '$' + localBudgetValue.toLocaleString()}
                            </span>
                        </div>
                        <input
                            type="range"
                            min="100"
                            max={totalCostAllProjects}
                            step="100"
                            value={localBudgetValue || (immediateBudgetValue !== 0 ? immediateBudgetValue : totalCostAllProjects)}
                            onChange={(e) => {
                                const value = Number(e.target.value);
                                // Update local state immediately for smooth UI
                                setLocalBudgetValue(value);
                                // Debounce the actual calculation
                                debouncedCalculation(value);
                            }}
                            className="help-me-choose__slider"
                        />
                    </div>
                ) : (

                    <div className={`help-me-choose__input-wrapper ${type}`}>
                        <span className="help-me-choose__input-value">
                            {localBudgetValue === 0 ? '$' + totalCostAllProjects.toLocaleString() : '$' + localBudgetValue.toLocaleString()}
                        </span>
                        <input
                            type="range"
                            min="100"
                            max={totalCostAllProjects}
                            step="100"
                            value={localBudgetValue || (immediateBudgetValue !== 0 ? immediateBudgetValue : totalCostAllProjects)}
                            onChange={(e) => {
                                const value = Number(e.target.value);
                                // Update local state immediately for smooth UI
                                setLocalBudgetValue(value);
                                // Debounce the actual calculation
                                debouncedCalculation(value);
                            }}
                            className="help-me-choose__slider"
                        />
                    </div>
                )}
            </>
        )
    }


    const renderDiyInput = () => {
        return (
            <div className="help-me-choose__diy-input">
                <div className="help-me-choose__toggle-wrapper">
                    <button
                        className={`help-me-choose__toggle-button ${!isDiyOnly ? 'active' : ''}`}
                        onClick={() => setIsDiyOnly(false)}
                    >
                        Open to Hiring a Professional
                    </button>
                    <button
                        className={`help-me-choose__toggle-button ${isDiyOnly ? 'active' : ''}`}
                        onClick={() => setIsDiyOnly(true)}
                    >
                        Prefer DIY Projects
                    </button>
                </div>
            </div>
        )
    }

    const renderRequiresDiscountsInput = () => {
        return (
            <div className="help-me-choose__diy-input">
                <div className="help-me-choose__toggle-wrapper">
                    <button
                        className={`help-me-choose__toggle-button ${!requiresDiscounts ? 'active' : ''}`}
                        onClick={() => setRequiresDiscounts(false)}
                    >
                        Open to All Projects
                    </button>
                    <button
                        className={`help-me-choose__toggle-button ${requiresDiscounts ? 'active' : ''}`}
                        onClick={() => setRequiresDiscounts(true)}
                    >
                        Prefer Projects with Discounts
                    </button>
                </div>
            </div>
        )
    }


    const budgetQuestionText = "What is your budget for home improvements?"
    const paybackQuestionText = "How many years can you wait for a return on investment?"
    const diyQuestionText = "Are you open to DIY-friendly projects or would you prefer to hire a professional?"
    const howManyProjectsQuestionText = "How many projects are you open to taking on?"
    const categoryQuestionText = "What parts of the house do you want to improve?"

    // Clean up debounced functions when component unmounts
    useEffect(() => {
        return () => {
            debouncedSetBudgetValue.cancel();
            debouncedSetPaybackYears.cancel();
            debouncedSetMaxProjects.cancel();
        };
    }, []);

    const multiProjectsViewToggles = [
        {
            id: 'list',
            label: 'See List',
        },
        {
            id: 'compare',
            label: 'Compare',
        }
    ]

    const comparisonFactors = [
        {
            id: 'cost',
            label: 'Cost',
            chartLabel: 'Cost',
            datapoint: 'project_total_cost',
            dataType: 'dollar',
            type: 'regular'
        },
        {
            id: 'payback',
            label: 'Payback',
            chartLabel: 'Payback',
            datapoint: 'project_total_payback',
            dataType: 'dollar',
            type: 'regular'
        },
        {
            id: 'roi',
            label: 'ROI',
            chartLabel: 'ROI',
            datapoint: 'project_roi',
            dataType: 'roi',
            type: 'regular'
        },
        {
            id: 'complexity',
            label: 'Project Complexity',
            chartLabel: 'Complexity',
            datapoint: 'project_complexity_refined',
            dataType: 'number',
            type: 'regular'
        },
        {
            id: 'disruption',
            label: 'Disruption to Home',
            chartLabel: 'Disruption',
            datapoint: 'project_disruption_refined',
            dataType: 'number',
            type: 'regular'
        },
        {
            id: 'diyFriendly',
            label: 'DIY-Friendly?',
            chartLabel: 'How DIY-Friendly is this project?',
            datapoint: 'project_diy_friendly',
            dataType: 'boolean',
            type: 'filter',
            columnOneName: 'DIY-Friendly',
            columnTwoName: 'Not DIY-Friendly'
        },
        {
            id: 'hasDiscounts',
            label: 'Known Discounts?',
            chartLabel: 'Are there identified discounts upfront?',
            datapoint: 'project_has_discounts',
            dataType: 'boolean',
            type: 'filter',
            columnOneName: 'Known Discounts',
            columnTwoName: 'No Discounts Found'
        }
    ]

    // useEffect that on initial mount sets the multiProjectsFilterOne and multiProjectsFilterTwo to the first two factors in the comparisonFactors array
    useEffect(() => {
        setMultiProjectsFilterOne(comparisonFactors[0]);
        setMultiProjectsFilterTwo(comparisonFactors[1]);
    }, []);

    useEffect(() => {
        // Get the ids from multiProjectsFilterOne and multiProjectsFilterTwo
        const filterOneId = multiProjectsFilterOne?.id;
        const filterTwoId = multiProjectsFilterTwo?.id;

        // Filter the comparisonFactors array to only include the factors that are not the ids of multiProjectsFilterOne and multiProjectsFilterTwo
        const newAvailableFactors = comparisonFactors.filter(factor => factor.id !== filterOneId && factor.id !== filterTwoId);
        setAvailableFactors(newAvailableFactors);

    }, [multiProjectsFilterOne, multiProjectsFilterTwo])

    const getProjectDescription = (project) => {

        const {
            project_total_cost = 0,
            project_cost_materials = 0,
            project_total_payback = 0,
            project_diy_friendly = false,
            project_discounts_company_specific = [],
            project_roi = 0,
            project_home_category_id = '',
            project_associated_climate_risks = []
        } = project

        const project_roi_no_labor = project_total_payback / project_cost_materials

        const climateRisks = propertyData?.data?.climate_risks || [];
        const relevantHouseArea = enrichedHouseAreasWithProjects.find(area => area.id === project_home_category_id) || {};
        const relevantHouseAreaRiskLevel = relevantHouseArea.risk_level_adjusted || 'low';

        // Get the applicable climate risks from the house area
        const relevantHouseAreaClimateRisks = relevantHouseArea.applicable_risks || [];

        // Use the strings in that list to filter the climateRisks list, matching the string to risk_id and return the objects
        const matchingClimateRisks = project_associated_climate_risks
            .map(risk => climateRisks.find(climateRisk => climateRisk.risk_name === risk.risk_name))
            .filter(risk => risk !== undefined);

        // From that matchingClimateRisks array get the count of objects that had a risk_level of 'High', 'Medium', or 'Low'
        const highRiskCount = matchingClimateRisks.filter(risk => risk.risk_level === 'High').length;
        const mediumRiskCount = matchingClimateRisks.filter(risk => risk.risk_level === 'Medium').length;
        const lowRiskCount = matchingClimateRisks.filter(risk => risk.risk_level === 'Low').length;


        const integerToWord = (number) => {
            if (number === 1) return 'one'
            if (number === 2) return 'two'
            if (number === 3) return 'three'
            if (number === 4) return 'four'
            if (number === 5) return 'five'
        }

        let firstSentenceQualifierRisk = ''
        let firstSentenceQualifierHouseArea = ''

        if (highRiskCount > 0 && mediumRiskCount > 0) {
            firstSentenceQualifierRisk = `${integerToWord(highRiskCount)} of your biggest climate threats, and ${integerToWord(mediumRiskCount)} of your moderate threats.`
        }
        else if (highRiskCount > 0) {
            firstSentenceQualifierRisk = `${integerToWord(highRiskCount)} of your biggest climate threats`
        } else if (mediumRiskCount > 0) {
            firstSentenceQualifierRisk = `${integerToWord(mediumRiskCount)} of your moderate threats`
        } else if (lowRiskCount > 0) {
            firstSentenceQualifierRisk = `${integerToWord(lowRiskCount)} of your lower threats`
        }

        if (relevantHouseAreaRiskLevel === 'high') {
            firstSentenceQualifierHouseArea = 'one of the most at-risk parts of your home.'
        } else if (relevantHouseAreaRiskLevel === 'medium') {
            firstSentenceQualifierHouseArea = 'a part of your home that is moderately at-risk.'
        } else if (relevantHouseAreaRiskLevel === 'low') {
            firstSentenceQualifierHouseArea = 'a part of your home that is has relatively less risk, though far from none.'
        }

        const firstSentence = `This project combats ${firstSentenceQualifierRisk} by increasing the resilience of ${firstSentenceQualifierHouseArea}`

        let secondSentenceQualifier1 = ''
        const wasROIPositive = project_roi > 0

        if (project_roi > 10) {
            secondSentenceQualifier1 = 'It has a massive return on your investment'
        } else if (project_roi > 5) {
            secondSentenceQualifier1 = 'It has a significant return on your investment'
        } else if (project_roi > 2) {
            secondSentenceQualifier1 = 'It has a moderate return on your investment'
        } else if (project_roi > 1) {
            secondSentenceQualifier1 = 'It has a small return on your investment'
        } else {
            secondSentenceQualifier1 = 'The financial benefit is limited'
        }

        let secondSentenceQualifier2 = ''
        let secondSentenceQualifier3 = ''
        if (project_total_cost <= 300) {
            if (project_roi > 2) {
                secondSentenceQualifier3 = 'and'
            } else {
                secondSentenceQualifier3 = 'but at least'
            }
            secondSentenceQualifier2 = 'is a very affordable project to take on. '
        } else if (project_total_cost <= 1000) {
            if (project_roi > 2) {
                secondSentenceQualifier3 = 'and'
            } else {
                secondSentenceQualifier3 = 'but at least'
            }
            secondSentenceQualifier2 = 'is a relatively affordable project to take on. '
        } else if (project_total_cost <= 3000) {
            if (project_roi > 2) {
                secondSentenceQualifier3 = 'but'
            } else {
                secondSentenceQualifier3 = 'and'
            }
            secondSentenceQualifier2 = 'is a bit more expensive to take on. '
        } else {
            if (project_roi > 2) {
                secondSentenceQualifier3 = 'but'
            } else {
                secondSentenceQualifier3 = 'and'
            }
            secondSentenceQualifier2 = 'is a very expensive project to take on. '
        }

        const secondSentence = `${secondSentenceQualifier1} ${secondSentenceQualifier3} ${secondSentenceQualifier2}`

        let thirdSentenceQualifier = ''
        let thirdSentenceQualifier2 = ''
        let thirdSentenceQualifier3 = ''

        if (project_diy_friendly) {
            thirdSentenceQualifier2 = "this project is DIY-friendly."
            if (project_roi > 2 && project_total_cost > 1000) {
                thirdSentenceQualifier = "Fortunately, "
            } else if (project_total_cost > 1000) {
                thirdSentenceQualifier = "Luckily,"
            } else if (project_roi > 2) {
                thirdSentenceQualifier = "Not only that,"
            } else {
                thirdSentenceQualifier = "Fortunately,"
            }

            if (project_roi_no_labor > 1 && project_roi < 1) {
                thirdSentenceQualifier3 = "Doing the work yourself would actually mean achieving a positive return on your investment."
            } else if (project_roi_no_labor > 1 && project_roi > 1) {
                if (project_roi_no_labor / project_roi > 1) {
                    thirdSentenceQualifier3 = "Doing the work yourself would mean increasing the return on your investment."
                } else if (project_roi_no_labor > 1) {
                    thirdSentenceQualifier3 = "Doing the work yourself would mean achieving a positive return on your investment."
                }
            } else if (project_roi_no_labor < 1) {
                thirdSentenceQualifier3 = "Unforunately, even doing the work yourself is unlikely to achieve a positive return on your investment."
            } else {
                thirdSentenceQualifier3 = "Unforunately, even doing the work yourself is unlikely to achieve a positive return on your investment."
            }

        } else {
            thirdSentenceQualifier = 'Unfortunately, this project is not DIY-friendly.'
        }

        const thirdSentence = `${thirdSentenceQualifier} ${thirdSentenceQualifier2} ${thirdSentenceQualifier3}`

        const finalSentence = `${firstSentence} ${secondSentence} ${thirdSentence}`


        return finalSentence
    }

    const ifYouOnlyChoose1ProjectOnlyOneAvailableText = "Based on your criteria, this is the only project available."
    const ifYouOnlyChoose1ProjectMultipleAvailableText = "If you were to only do 1 project, we'd recommend..."
    const ifYouDoMultipleProjectsText = "But Your Budget Allows for More!"
    const ifYouDoMultipleProjectsdescription = `Your budget would actually cover${filteredProjects.length === projects.length ? " all " : ""} ${filteredProjects.length} projects${filteredProjects.length > 3 ? "!" : "."} We've listed them below and made it easy to compare based on whatever metrics you prefer.`

    const handleMultiProjectsFilterClear = () => {
        setMultiProjectsFilterOne(null);
        setMultiProjectsFilterTwo(null);
    }

    const handleShowProjectFilterOptions = () => {
        setShowProjectFilterOptions(!showProjectFilterOptions);
    }

    return (
        <div className={`taking-action-section-container ${deviceType}`}>
            {/* <h1>Help Me Choose</h1> */}
            {!questionnaireCompleted ?
                <HelpMeChooseQuestionnaire
                    budgetInput={renderBudgetInput()}
                    // paybackInput={renderPaybackInput()}
                    diyInput={renderDiyInput()}
                    // maxProjectsInput={renderMaxProjectsInput()}
                    // categoryInput={renderCategoriesInput()}
                    totalProjects={totalProjects}
                    setQuestionnaireCompleted={setQuestionnaireCompleted}
                    requiresDiscountsInput={renderRequiresDiscountsInput()}
                />
                :
                <div className="help-me-choose__content-frame">

                    {/* <div className="help-me-choose__filters-container">

                        <div className={`help-me-choose__individual-filter regular-filter comparison`}>
                            <div className="help-me-choose__filter-label">Your Budget: </div>
                            {renderBudgetInput('comparison')}
                        </div>
                    </div> */}
                    {filteredProjects.length > 0 ?
                        <>
                            <div className="help-me-choose__projects-breakdown-container">
                                <h4>{filteredProjects.length === 1 ? ifYouOnlyChoose1ProjectOnlyOneAvailableText : ifYouOnlyChoose1ProjectMultipleAvailableText}</h4>
                                <div className={`help-me-choose__projects-only-do-one-content-row ${deviceType}`}>
                                    <ProjectsListCard key={filteredProjects[0].id} project={filteredProjects[0]} allowDragging={true} handleProjectClick={handleProjectClick}/>
                                    <div className="help-me-choose__project-description-container">
                                        <p className="help-me-choose__project-description text-align-center">{getProjectDescription(filteredProjects[0])}</p>
                                    </div>
                                </div>
                            </div>
                            {filteredProjects.length > 1 && (
                                <div className="help-me-choose__projects-breakdown-container">
                                    <h4>{ifYouDoMultipleProjectsText}</h4>
                                    <p className="help-me-choose__projects-description-container text-align-center">{ifYouDoMultipleProjectsdescription}</p>
                                    {multiProjectsView === 'list' && (
                                        <div className="help-me-choose__projects-list-container">
                                            {filteredProjects.map(project => (
                                                <ProjectsListCard key={project.id} project={project} allowDragging={true} handleProjectClick={handleProjectClick}/>
                                            ))}
                                        </div>
                                    )}
                                    {multiProjectsView === 'compare' && (
                                        <>
                                            <div className={`help-me-choose__filters-container ${deviceType}`}>
                                                {!showProjectFilterOptions && (
                                                    <div className={`help-me-choose__individual-filter regular-filter comparison`}>
                                                        <div className="help-me-choose__filter-label comparison-label">Your Budget: </div>
                                                        {renderBudgetInput('comparison')}
                                                    </div>
                                                )}
                                                <div className="help-me-choose__projects-compare-container">
                                                    <DndContext
                                                        onDragStart={handleDragStart}
                                                        onDragEnd={handleDragEnd}
                                                        onDragCancel={handleDragCancel}
                                                    >
                                                        <div className={`help-me-choose__multi-projects-filters-droppable-container ${deviceType} ${showProjectFilterOptions ? 'filter-options-showing' : 'filter-options-hidden'}`}>
                                                            <div className="help-me-choose__multi-projects-filters-list-container">
                                                                <span>{showProjectFilterOptions ? 'How do you want to compare these projects?' : 'Comparing Based On:'}</span>
                                                            </div>
                                                            <div className={`help-me-choose__multi-projects-filters-what-is-being-compared-container ${deviceType}`}>
                                                                <DroppableArea id="drop-area-1">
                                                                    <div className={`help-me-choose__multi-projects-filters-droppable-item ${isDragging ? 'is-moving' : ''} ${!multiProjectsFilterOne && 'empty'}`}>
                                                                        <span>
                                                                            {multiProjectsFilterOne
                                                                                ? multiProjectsFilterOne.label
                                                                                : 'Drop a Metric Here'}
                                                                        </span>
                                                                    </div>
                                                                </DroppableArea>
                                                                <span className="help-me-choose__multi-projects-filters-droppable-item-vs">vs</span>
                                                                <DroppableArea id="drop-area-2">
                                                                    <div className={`help-me-choose__multi-projects-filters-droppable-item ${isDragging ? 'is-moving' : ''} ${!multiProjectsFilterTwo && 'empty'}`}>
                                                                        <span>
                                                                            {multiProjectsFilterTwo
                                                                                ? multiProjectsFilterTwo.label
                                                                                : 'Drop a Metric Here'}
                                                                        </span>
                                                                    </div>
                                                                </DroppableArea>

                                                            </div>
                                                            {/* {showProjectFilterOptions && (
                                                                <div className="help-me-choose__multi-projects-filters-show-clear-button clear" onClick={handleMultiProjectsFilterClear}>
                                                                    <span>Clear</span>
                                                                </div>
                                                            )} */}
                                                            {!showProjectFilterOptions && (
                                                                <div className="help-me-choose__multi-projects-filters-show-clear-button show-options" onClick={handleShowProjectFilterOptions}>
                                                                    <span>Change</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                        {showProjectFilterOptions && (
                                                            <div className="help-me-choose__multi-projects-filters-draggable-container">
                                                                <div className={`help-me-choose__multi-projects-filters-list ${deviceType}`}>
                                                                    {availableFactors.map(factor => (
                                                                        <DraggableFactor
                                                                            key={factor.id}
                                                                            factor={factor}
                                                                        >
                                                                            <div className="help-me-choose__multi-projects-filters-list-item">
                                                                                <span>{factor.label}</span>
                                                                            </div>
                                                                        </DraggableFactor>
                                                                    ))}
                                                                </div>
                                                                <span className="help-me-choose__multi-projects-filters-draggable-explainer-text text-align-center italic-text ">Drag and drop the metrics you want to use to compare the projects</span>
                                                                <div className="help-me-choose__multi-projects-filters-show-clear-button close" onClick={() => {
                                                                    if (multiProjectsFilterOne && multiProjectsFilterTwo) {
                                                                        handleShowProjectFilterOptions()
                                                                    } else {
                                                                        null;
                                                                    }
                                                                }}>
                                                                    Hide Options
                                                                    <ChevronsUp />
                                                                </div>
                                                            </div>
                                                        )}
                                                    </DndContext>
                                                </div>
                                            </div>
                                            <div className={`help-me-choose__comparison-chart-container ${deviceType}`}>
                                                <ComparisonChart
                                                    projects={filteredProjects}
                                                    multiProjectsFilterOne={multiProjectsFilterOne}
                                                    multiProjectsFilterTwo={multiProjectsFilterTwo}
                                                    hoveredProjectId={hoveredProjectId}
                                                    setHoveredProjectId={setHoveredProjectId}
                                                />
                                            </div>
                                            <div className="help-me-choose__projects-list-container">
                                                {filteredProjects.map(project => (
                                                    <div
                                                        key={project.project_id}
                                                        className={`help-me-choose__projects-list-item-frame ${deviceType}`}
                                                        onMouseEnter={() => setHoveredProjectId(project.project_id)}
                                                        onMouseLeave={() => setHoveredProjectId(null)}
                                                    >
                                                        <div
                                                            className={`help-me-choose__projects-list-item-frame-marker ${deviceType} ${(hoveredProjectId && hoveredProjectId !== project.project_id) ? 'opaque' : ''}`}
                                                            style={{ backgroundColor: project.color }}
                                                        />
                                                        <ProjectsListCard key={project.id} project={project} allowDragging={true} handleProjectClick={handleProjectClick}/>
                                                    </div>
                                                ))}
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}

                        </>
                        :
                        <p className='text-align-center'>No projects fit your criteria. Try adjusting them to see what is possible!</p>
                    }
                </div>
            }
        </div>
    );
};

export default HelpMeChoose;


// <div className="help-me-choose__content-container">
//     <DndContext
//         sensors={sensors}
//         collisionDetection={closestCenter}
//         onDragEnd={handleDragEnd}
//         modifiers={[restrictToVerticalAxis, restrictToFirstScrollableAncestor]}
//     >
//         <SortableContext
//             items={availableFilters.map(f => f.id)}
//             strategy={verticalListSortingStrategy}
//         >
//             <div className="help-me-choose__filters-container">
//                 {filterOptions.map(filter => {

//                     const regular_filters = ['buget', 'diy', 'requires_discounts']
//                     return (
//                         // <SortableFilter key={filter.id} id={filter.id}>
//                         <div className={`help-me-choose__individual-filter ${regular_filters.includes(filter.id) ? 'regular-filter' : 'extended-filter'}`}>
//                             <div className="help-me-choose__filter-label">{filter.label}</div>
//                             {filter.id === 'buget' && (
//                                 renderBudgetInput()
//                             )}
//                             {/* {filter.id === 'payback' && (
//                                                     renderPaybackInput()
//                                                 )}
//                                                 {filter.id === 'max_projects' && (
//                                                     renderMaxProjectsInput()
//                                                 )} */}
//                             {filter.id === 'diy' && (
//                                 renderDiyInput()
//                             )}
//                             {filter.id === 'requires_discounts' && (
//                                 renderRequiresDiscountsInput()
//                             )}
//                             {/* {filter.id === 'house_category' && (
//                                                     renderCategoriesInput()
//                                                 )} */}
//                         </div>
//                         // </SortableFilter>
//                     )
//                 })}
//             </div>
//         </SortableContext>
//     </DndContext>
//     {/* <div className="help-me-choose__projects-list">
//                             {selectedFilters.map((filter) => (
//                                 <div key={filter} className="help-me-choose__selected-filter">
//                                     <button onClick={() => removeSelectedFilter(filter)}>{filter}</button>
//                                 </div>
//                             ))}
//                         </div> */}
// </div>

// const renderPaybackInput = () => {
//     return (
//         <div className="help-me-choose__input-wrapper">
//             <span className="help-me-choose__input-value">
//                 {immediatePaybackYears} {immediatePaybackYears === 1 ? 'year' : 'years'}
//             </span>
//             <input
//                 type="range"
//                 min="1"
//                 max="10"
//                 value={immediatePaybackYears}
//                 onChange={(e) => {
//                     const value = Number(e.target.value);
//                     setImmediatePaybackYears(value);
//                     debouncedSetPaybackYears(value);
//                 }}
//                 className="help-me-choose__slider"
//             />
//         </div>
//     )
// }

// const renderMaxProjectsInput = () => {
//     return (
//         <div className="help-me-choose__input-wrapper">
//             <span className="help-me-choose__input-value">
//                 {immediateMaxProjects}
//             </span>
//             <input
//                 type="range"
//                 min="1"
//                 max={totalProjects}
//                 value={immediateMaxProjects}
//                 onChange={(e) => {
//                     const value = Number(e.target.value);
//                     setImmediateMaxProjects(value);
//                     debouncedSetMaxProjects(value);
//                 }}
//                 className="help-me-choose__slider"
//             />
//         </div>
//     )
// }

// const renderCategoriesInput = () => {
//     return (
//         <div className="help-me-choose__categories-input">
//             {uniqueHomeCategories.map(category => (
//                 <label key={category} className="help-me-choose__category-option">
//                     <input
//                         type="checkbox"
//                         checked={selectedCategories.includes(category)}
//                         onChange={() => {
//                             setSelectedCategories(prev =>
//                                 prev.includes(category)
//                                     ? prev.filter(c => c !== category)
//                                     : [...prev, category]
//                             );
//                         }}
//                     />
//                     <span className="help-me-choose__category-label">{category}</span>
//                 </label>
//             ))}
//         </div>
//     )
// }

{/* {filterOptions.map(filter => {

                            const regular_filters = ['buget', 'diy', 'requires_discounts']
                            return (
                                <div className={`help-me-choose__individual-filter ${regular_filters.includes(filter.id) ? 'regular-filter' : 'extended-filter'}`}>
                                    {filter.id === 'buget' && (
                                        <>
                                            <div className="help-me-choose__filter-label">{filter.label}</div>
                                            renderBudgetInput('comparison')
                                        </>
                                    )}
                                    {filter.id === 'diy' && (
                                        renderDiyInput()
                                    )}
                                    {filter.id === 'requires_discounts' && (
                                        renderRequiresDiscountsInput()
                                    )}
                                </div>
                            )
                        })} */}